import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import Dropzone, { type DropzoneRef } from 'react-dropzone'
import { HiMiniXMark, HiOutlineDocumentArrowUp } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import { Thumbnail } from '@/components/common/Attachment/Thumbnail'
import Chip from '@/components/common/Chip'
import Spinner from '@/components/common/Spinner'

import {
  ALLOWED_MIME_TYPES,
  SPREADSHEET_MIME_TO_EXTENSIONS,
} from '@/constants/file'

import {
  cleanFileName,
  getFileExtensionFromName,
  getFileSizeAsHumanReadable,
} from '@/helpers/file'

export const MAX_FILE_SIZE: number = 150 * 1000 * 1000 // 150 MB

interface FileUpload {
  onAddFiles: (files: Array<File>) => void
  className?: string
  disabled?: boolean
  isLoading?: boolean
  imagesOnly?: boolean
  accepts?: string[]
  maxFiles?: number
  children?: (props: {
    getRootProps: () => React.HTMLAttributes<HTMLElement>
    getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>
    open: () => void
  }) => React.ReactNode | React.ReactNode
}

interface LocalFileProps {
  file: File
  className?: string
  onDelete?: () => void
}

export const LocalFile: React.FC<LocalFileProps> = ({
  file,
  className,
  onDelete,
}) => {
  const extension = getFileExtensionFromName(file.name)
  return (
    <div
      className={twMerge(
        'relative group/directive flex items-center gap-x-2 max-w-full',
        className,
      )}
    >
      <div className="relative flex items-center justify-center h-fit">
        <Thumbnail type={extension} className="p-[9px]" />
        <div className="absolute -top-[8px] -left-[6px] opacity-0 group-hover/directive:opacity-100 transition-opacity duration-100">
          <button
            type="button"
            onClick={onDelete}
            className="p-[1px] rounded-full border border-primary-300 dark:border-primary-700 bg-primary-100 dark:bg-primary-800 hover:bg-primary-200 dark:hover:bg-primary-700"
          >
            <HiMiniXMark className="w-[14px] h-[14px] text-primary-900 dark:text-primary-100" />
          </button>
        </div>
      </div>
      <div className="flex flex-col leading-5 line-clamp-1">
        <span className="text-sm font-medium text-primary-900 dark:text-primary-100 line-clamp-1">
          {cleanFileName(file.name)}
        </span>
        <span className="text-[13px] text-primary-700 dark:text-primary-300">
          <span>{getFileSizeAsHumanReadable(file.size)}</span>
        </span>
      </div>
    </div>
  )
}

export const CompactLocalFile: React.FC<LocalFileProps> = ({
  file,
  className,
  onDelete,
}) => {
  return (
    <Chip
      className={twMerge(
        'flex items-center gap-2 p-[1px] px-2 max-w-full rounded-full',
        className,
      )}
      onDelete={onDelete}
    >
      <div className="flex items-center gap-[6px] ml-1 truncate">
        <CompactThumbnail name={file.name} size={14} />
        <span className="text-[13px] truncate">{cleanFileName(file.name)}</span>
      </div>
    </Chip>
  )
}

export const getAcceptedTypes = (imagesOnly: boolean, accepts?: string[]) => {
  if (imagesOnly) {
    return {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
    }
  }
  const acceptedTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
    'text/plain': ['.txt'],
    'application/pdf': ['.pdf'],
    'application/rtf': ['.rtf'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template': [
      '.dotx',
    ],
    'application/msword': ['.doc'],
    'application/vnd.ms-excel': ['.xls'],
    ...SPREADSHEET_MIME_TO_EXTENSIONS,
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      ['.pptx'],
    'text/html': ['.html', '.htm'],
    'text/markdown': ['.md', '.markdown'],
  }
  if (accepts) {
    return Object.fromEntries(
      Object.entries(acceptedTypes).filter(([key]) => accepts.includes(key)),
    )
  }
  return acceptedTypes
}

const FileUpload = forwardRef<DropzoneRef, FileUpload>(
  (
    {
      onAddFiles,
      className,
      disabled = false,
      isLoading = false,
      imagesOnly = false,
      maxFiles,
      children,
      accepts,
    },
    ref,
  ) => {
    const dropzoneRef = useRef<DropzoneRef>(null)

    useImperativeHandle(ref, () => ({
      open: () => dropzoneRef.current?.open(),
    }))

    const accept = getAcceptedTypes(imagesOnly, accepts)

    const handleAddFiles = async (files: Array<File>) => {
      let allowedTypes = ALLOWED_MIME_TYPES
      if (accepts) {
        allowedTypes = allowedTypes.filter((type) => accepts.includes(type))
      }
      if (imagesOnly) {
        allowedTypes = allowedTypes.filter((type) => type.includes('image'))
      }

      const allowedExtensions = Object.values(accept).flat()
      const sizedFiles = files.filter((file) => {
        if (file.size >= MAX_FILE_SIZE) {
          alert('Uploaded files must be less than 150mb!')
          return false
        }
        return true
      })
      const validFiles = sizedFiles.filter((file) => {
        const extension = `.${getFileExtensionFromName(file.name)}`
        return (
          allowedTypes.includes(file.type) ||
          allowedExtensions.includes(extension)
        )
      })
      if (validFiles.length < sizedFiles.length) {
        alert('Files with an unsupported type are not allowed.')
      }
      const nonEmptyFiles = validFiles.filter((file) => file.size > 0)
      if (nonEmptyFiles.length < validFiles.length) {
        alert('Files with no content are not allowed.')
      }
      onAddFiles(nonEmptyFiles)
    }

    if (isLoading) {
      return (
        <div className="flex flex-col justify-center items-center px-8 py-[52px] gap-2 w-full bg-primary-50 dark:bg-primary-800/50 rounded-lg border border-dashed border-primary-300 dark:border-primary-700">
          <div className="flex items-center justify-center gap-x-2 pt-1">
            <Spinner className="w-5 h-5" />
            <span className="text-md font-medium">Uploading files...</span>
          </div>
        </div>
      )
    }

    return (
      <div>
        <Dropzone
          ref={dropzoneRef}
          onDrop={handleAddFiles}
          accept={accept}
          disabled={disabled}
          maxFiles={maxFiles}
          // This zone handles its own drop, so the event must not reach an
          // ancestor that also handles one — FullScreenDropzone listens on
          // `window`, and would add the same files a second time.
          noDragEventsBubbling={true}
        >
          {({ getRootProps, getInputProps, open }) => (
            <div className="relative">
              {children ? (
                children({ getRootProps, getInputProps, open })
              ) : (
                <div
                  {...getRootProps()}
                  className={twMerge(
                    'flex flex-col justify-center items-center p-8 pt-9 gap-1 w-full transition duration-100 bg-primary-50 hover:bg-primary-100/80 dark:bg-primary-800/50 dark:hover:bg-primary-800/30 rounded-lg border border-dashed border-primary-300 dark:border-primary-700',
                    disabled && 'opacity-60 pointer-events-none',
                    className ?? '',
                  )}
                >
                  <input {...getInputProps()} />
                  <HiOutlineDocumentArrowUp className="flex-shrink-0 h-5 w-5 text-primary-500 dark:text-primary-400 mb-1" />
                  <span className="text-sm text-primary-500 dark:text-primary-400 font-normal">
                    Drag and drop files here
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      open()
                    }}
                    className={twMerge(
                      'text-sm text-primary-700 dark:text-primary-300 underline',
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                    disabled={disabled}
                  >
                    or browse for files
                  </button>
                </div>
              )}
            </div>
          )}
        </Dropzone>
      </div>
    )
  },
)

FileUpload.displayName = 'FileUpload'

export default FileUpload
